import { Component } from '@angular/core';
import { Select, Store } from '@ngxs/store';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import {
    AlertService,
    ButtonColors,
    ButtonModule,
    DropdownMenuItem,
    LayerService,
    LayerSize,
    PopupService,
} from '@ed---interne/ng-uui-components';
import { SubcontractorInvoiceModel, ProjectModel } from '../../../project.models';
import { ProjectsState } from '../../../project.state';
import { CommonModule } from '@angular/common';
import { SharedModule } from 'src/app/shared/shared.module';
import { RouterModule } from '@angular/router';
import { Observable } from 'rxjs';
import { DisplayType } from 'src/app/shared/components/ed-table/ed-table.component';
import { MatTableDataSource } from '@angular/material/table';
import { LayerPopupOptions } from 'src/app/shared/all.constants';
import { SubcontractorInvoiceUpdate } from './subcontracting-update/subcontracting-update.component';
import { SubcontractorInvoiceDelete } from './subcontracting-delete/subcontracting-delete.component';
import { SubcontractorInvoiceGenerateRecurrence } from './subcontracting-generate-recurrence/subcontracting-generate-recurrence.component';
import { SubcontractorInvoiceAdd } from './subcontracting-add/subcontracting-add.component';

export enum BillingOutputEvent {
    EDIT = 'EDIT',
    DELETE = 'DELETE',
}

@UntilDestroy()
@Component({
    selector: 'project-subcontracting',
    templateUrl: './subcontracting.component.html',
    styleUrls: ['./subcontracting.component.scss'],
    standalone: true,
    schemas: [],
    imports: [CommonModule, SharedModule, ButtonModule, RouterModule],
    providers: [],
})
export class ProjectSubcontractingComponent {
    @Select(ProjectsState.projectWithDetail)
    public project$!: Observable<ProjectModel>;
    public project: ProjectModel | undefined;

    public buttonColors = ButtonColors;

    public subcontractorInvoiceData = new MatTableDataSource<{}>([]);
    public subcontractorInvoiceColumns = [
        {
            displayType: DisplayType.DATE,
            objectDisplayName: 'Date prévue',
            objectKey: 'plannedDate',
            bold: false,
        },
        {
            displayType: DisplayType.NUMBER,
            objectDisplayName: 'Montant HT',
            objectKey: 'amountExclTax',
            bold: true,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Facture émise',
            objectKey: 'isSentLabel',
            bold: false,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Motif',
            objectKey: 'comment',
            bold: false,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Code facture',
            objectKey: 'invoiceNumber',
            bold: false,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Abonnement ?',
            objectKey: 'isSubscriptionLabel',
            bold: false,
        },
        {
            displayType: DisplayType.DATE,
            objectDisplayName: 'Date de début',
            objectKey: 'startDate',
            bold: false,
        },
        {
            displayType: DisplayType.DATE,
            objectDisplayName: 'Date de fin',
            objectKey: 'endDate',
            bold: false,
        },
    ];

    constructor(
        private readonly layerService: LayerService,
        private readonly popupService: PopupService,
        private readonly store: Store,
        private readonly alertService: AlertService,
    ) {
        this.project$.pipe(untilDestroyed(this)).subscribe((project: ProjectModel) => {
            if (!project) return;
            this.project = project;

            this.subcontractorInvoiceData = new MatTableDataSource<{}>(
                project.subcontractorInvoices?.map((a) => ({
                    ...a,
                    isSentLabel: a.isSent ? 'Oui' : 'Non',
                    isSubscriptionLabel: a.isSubscription ? 'Oui' : 'Non',
                    plannedDate: new Date(a.plannedDate),
                    actions: this.getActions(),
                })),
            );
        });
    }

    getActions(): DropdownMenuItem[] {
        return [
            {
                icon: 'bi bi-pen',
                text: 'Modifier',
                outputEventString: BillingOutputEvent.EDIT,
            },
            {
                icon: 'bi bi-trash',
                text: 'Supprimer',
                outputEventString: BillingOutputEvent.DELETE,
            },
        ];
    }

    public onActionClicked(billingId: any, event: string): void {
        switch (event) {
            case BillingOutputEvent.EDIT:
                this.updateSubcontractorInvoice(billingId);
                break;
            case BillingOutputEvent.DELETE:
                this.deleteSubcontractorInvoices(billingId);
                break;
            default:
                break;
        }
    }
    public updateSubcontractorInvoice(billingId: any) {
        if (!this.project) return;
        const header = 'Mettre à jour la facture de sous-traitance';
        const billingToEdit = this.project.subcontractorInvoices?.find((b) => b.id === billingId);
        this.layerService.show(
            SubcontractorInvoiceUpdate,
            { header, project: this.project, billingToEdit },
            { size: LayerSize.Medium, withClosePopup: LayerPopupOptions.Editing },
        );
    }
    public deleteSubcontractorInvoices(billing: SubcontractorInvoiceModel) {
        if (!this.project) return;
        this.layerService.show(
            SubcontractorInvoiceDelete,
            { project: this.project, billing },
            { size: LayerSize.Medium, withClosePopup: LayerPopupOptions.Editing },
        );
    }

    public addSubcontractorInvoice() {
        if (!this.project) return;
        const header = 'Importer des factures de sous-traitance';
        this.layerService.show(
            SubcontractorInvoiceAdd,
            { header, project: this.project },
            { size: LayerSize.Large, withClosePopup: LayerPopupOptions.Creating },
        );
    }

    public addSubcontractorInvoiceRecurrence() {
        if (!this.project) return;
        const header = 'Anticiper une récurrence';
        this.layerService.show(
            SubcontractorInvoiceGenerateRecurrence,
            { header, project: this.project },
            { size: LayerSize.Medium, withClosePopup: LayerPopupOptions.Creating },
        );
    }
}
